import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Avatar from '@atlaskit/avatar/Avatar';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { fg } from '@confluence/feature-gating';

import { i18n } from './i18n.ts';
import type { UserShape } from './ownership-models.ts';
import { PageOwnershipByLineWithContributorsDropdown } from './PageOwnershipByLineWithContributorsDropdown/index.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledBox = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.025'),

	color: token('color.text.subtle'),
	fontWeight: token('font.weight.regular'),
	borderRadius: token('radius.small'),

	paddingRight: token('space.050'),
	height: '24px',

	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		cursor: 'pointer',
	},

	'&:active': {
		backgroundColor: token('color.background.neutral.subtle.pressed'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'span[data-testid="badge-style-avatar--inner"]': {
		height: '20px',
		width: '20px',
	},
});

type PageOwnershipBylineContentProps = {
	owner: UserShape | null;
	author: UserShape;
	contributorCountWithoutOwner: number;
	testId?: string;
	analyticsSource: string;
	generatedBy?: string | null;
};

export const BadgeStylePageOwnershipBylineComponent = ({
	owner,
	author,
	testId,
	analyticsSource,
	generatedBy,
}: PageOwnershipBylineContentProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const pageOwner = owner ?? author;
	const avatarSrc = pageOwner.isAnonymous || !pageOwner.avatarUrl ? undefined : pageOwner.avatarUrl;

	const handleAnalytics = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'contentOwnerBadge',
				source: analyticsSource,
			},
		}).fire();
	};

	return (
		<Tooltip
			content={intl.formatMessage(i18n.abbreviatedBylineOwnerMessage)}
			hideTooltipOnClick
			delay={0}
		>
			{(tooltipProps) => (
				<ProfileCardWrapper
					trigger="click"
					userId={pageOwner.userId}
					ariaLabel={intl.formatMessage(
						generatedBy === 'ONBOARDING' && fg('cc-activation-onboarding-byline')
							? i18n.bylineMainTextCreatedByConfluence
							: i18n.abbreviatedBylineMainText,
						{
							user: pageOwner.fullName,
						},
					)}
				>
					<StyledBox {...tooltipProps} data-testId={testId} onClick={handleAnalytics}>
						<Avatar
							src={avatarSrc}
							borderColor="transparent"
							size="xsmall"
							testId="badge-style-avatar"
						/>
						<FormattedMessage
							{...(generatedBy === 'ONBOARDING' && fg('cc-activation-onboarding-byline')
								? i18n.bylineMainTextCreatedByConfluence
								: i18n.abbreviatedBylineMainText)}
							values={{ user: pageOwner.fullName }}
						/>
					</StyledBox>
				</ProfileCardWrapper>
			)}
		</Tooltip>
	);
};

export const BadgeStylePageOwnershipByline = (props: PageOwnershipBylineContentProps) => {
	if (fg('page_byline_owner_and_contributors_dropdown')) {
		return <PageOwnershipByLineWithContributorsDropdown {...props} />;
	}

	return <BadgeStylePageOwnershipBylineComponent {...props} />;
};
